import { useRoute, reactive, computed, defineComponent, getCurrentInstance, useContext, useCssModule, } from '@nuxtjs/composition-api';
import { useTask } from 'vue-concurrency';
import { createHead, useAsyncRef } from '~/utils';
import { createGridChunks, getArticlesSize } from '~/composables';
import { presets } from '~/composables/grid/franchise';
import { useDuck } from '~/services';
import { FRANCHISE_PROMO_CTA } from '~/pages/franchises/data';
/**
 * Duck service.
 */
const duck = useDuck();
/**
 * Columns
 */
export const columns = {
    desktop: 2,
    desktopMedium: 2,
    desktopWide: 3,
    mobile: 1,
    mobileMedium: 1,
    mobileWide: 1,
    tablet: 2,
};
const TABS = [
    {
        title: 'Все',
        theme: 'lighter',
        to: '/franchises',
        value: 'franchises',
    },
    {
        title: 'Истории',
        theme: 'lighter',
        to: '/franchises/stories',
        value: 'franchises/stories',
    },
    {
        title: 'Портреты',
        theme: 'lighter',
        to: '/franchises/portraits',
        value: 'franchises/portraits',
    },
    {
        title: 'Обзоры',
        theme: 'lighter',
        to: '/franchises/reviews',
        value: 'franchises/reviews',
    },
    {
        title: 'Инструкции',
        theme: 'lighter',
        to: '/franchises/instructions',
        value: 'franchises/instructions',
    },
    // {
    //   title: 'Журналы',
    //   theme: 'lighter',
    //   to: '/franchises/magazines',
    //   value: 'franchises/magazines',
    // },
    // {
    //   title: 'Фильмы',
    //   theme: 'lighter',
    //   to: '/franchises/films',
    //   value: 'franchises/films',
    // },
    {
        title: 'Рейтинги',
        theme: 'lighter',
        to: '/franchises/ratings',
        value: 'franchises/ratings',
    },
];
export default defineComponent({
    name: 'PageFranchiseIndex',
    setup() {
        /**
         * Composables.
         */
        const context = useContext();
        const css = useCssModule();
        const route = useRoute();
        const viewport = context.$viewport;
        /**
         * Instance.
         */
        const instance = getCurrentInstance();
        /**
         * Articles.
         */
        const articles = useAsyncRef(instance, 'articles');
        const isDone = useAsyncRef(instance, 'isDone');
        /**
         * Current preset.
         */
        const articlesCards = computed(() => getArticlesSize(viewport.breakpoint, {
            columns,
            presets,
        }));
        /**
         * Grids.
         */
        const grids = computed(() => createGridChunks(articles.value, articlesCards.value));
        const pageOffset = useAsyncRef(instance, 'pageOffset');
        const currentTab = useAsyncRef(instance, 'currentTab');
        const tabs = reactive({
            currentTab,
            tabs: computed(() => {
                return TABS.map((tab) => {
                    return {
                        ...tab,
                        theme: tab.value === route.value.path.replace(/^\/|\/$/g, '') ? 'yellow' : 'lighter',
                    };
                });
            }),
            change(tab) {
                const { theme = '', title, to, value } = tab;
                tabs.currentTab = {
                    theme,
                    title,
                    to,
                    value,
                };
            },
        });
        /**
         * Fetch articles task.
         */
        const fetchArticlesTask = useTask(function* (signal) {
            if (fetchArticlesTask.isRunning || isDone.value) {
                return;
            }
            signal.addEventListener('abort', () => duck.abortRequest('getPubListById'));
            try {
                let newArticles = [];
                const { data } = yield duck.pub.getPubTax({
                    'list[limit]': String(articlesCards.value),
                    'list[offset]': String(articles.value.length * (pageOffset.value + 1)),
                    'tax[url]': route.value.path.replace(/^\/|\/$/g, ''),
                });
                newArticles = data.articles;
                if (newArticles.length < articlesCards.value) {
                    isDone.value = true;
                }
                if (!newArticles.length) {
                    return;
                }
                articles.value = [...articles.value, ...newArticles];
            }
            catch (error) {
                console.error(error);
            }
        });
        function handleGridIntersect(gridKey) {
            gridKey = gridKey + 1 + pageOffset.value;
            history.replaceState(null, '', gridKey > 1 ? `?page=${gridKey}` : context.route.value.fullPath);
        }
        const listsVisibility = useAsyncRef(instance, 'listsVisibility');
        function getListsVisibility(gridKey) {
            return gridKey === 1
                ? listsVisibility.value
                : Object.keys(listsVisibility.value).reduce((items, key) => {
                    items[key] = false;
                    return items;
                }, {});
        }
        return {
            articlesCards,
            css,
            columns,
            currentTab,
            fetchArticlesTask,
            grids,
            handleGridIntersect,
            isDone,
            presets,
            tabs,
            FRANCHISE_PROMO_CTA,
            listsVisibility,
            getListsVisibility,
        };
    },
    async asyncData(context) {
        const { app, env, error, route } = context;
        try {
            let isDone = false;
            const path = route.path.replace(/^\/|\/$/g, '');
            const currentTab = TABS.find((tab) => tab.value === path);
            const limit = getArticlesSize(app.$viewport.breakpoint, {
                columns,
                presets,
            });
            const pageNumber = Number(route.query.page) || 0;
            const pageOffset = pageNumber > 0 ? pageNumber - 1 : 0;
            let articles = [];
            const { data } = await duck.pub.getPubTax({
                'list[limit]': limit,
                'list[offset]': limit * pageOffset,
                'tax[url]': path,
            });
            if (!data?.articles?.length) {
                throw new Error('Not found');
            }
            if (data.articles.length < limit) {
                isDone = true;
            }
            articles = data.articles;
            const lists = {
                'franchises-partner': {
                    'slider:rubric-partner': true,
                    'slider-mobile:rubric-partner': true,
                },
                'franchises-news': {
                    'slider:interesting-today': true,
                    'slider-mobile:interesting-today': true,
                },
            };
            const listsData = {};
            const listsVisibility = {};
            if (Object.keys(lists).length) {
                const tasks = await Promise.allSettled(Object.keys(lists).map(async (id) => {
                    const { data } = await duck.pub.getPubListById(id);
                    return data;
                }));
                for (const task of tasks) {
                    let cardsKeys;
                    if (task.status === 'fulfilled') {
                        cardsKeys = lists[task?.value?.slug || ''];
                        if (!cardsKeys) {
                            continue;
                        }
                        for (const cardKey of Object.keys(cardsKeys)) {
                            listsData[cardKey] = task.value;
                            listsVisibility[cardKey] = true;
                        }
                    }
                    else {
                        const splittedUrl = task?.reason?.url?.split('/') || [];
                        if (!splittedUrl.length) {
                            continue;
                        }
                        const slug = splittedUrl[splittedUrl.length - 1];
                        cardsKeys = lists[slug];
                        if (!cardsKeys) {
                            continue;
                        }
                        for (const cardKey of Object.keys(cardsKeys)) {
                            listsVisibility[cardKey] = false;
                        }
                    }
                }
            }
            const title = `${currentTab.title} | Forbes Франшизы | Forbes.ru`;
            const head = {
                canonical: env.HOST + route.path,
                title,
                url: env.HOST + route.path,
            };
            context.$page = app.$page = {
                title,
            };
            return {
                $page: context.$page,
                articles,
                currentTab,
                head,
                isDone,
                pageOffset,
                title,
                listsData,
                listsVisibility,
            };
        }
        catch (e) {
            console.error(e);
            error({ statusCode: 404 });
        }
    },
    head() {
        return createHead(this.head, { shouldUseTitleTemplate: false });
    },
});
